import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Star, Wrench } from "lucide-react";
import { COMPANY, GALLERY_IMAGES } from "@/lib/constants";

const HERO_VIDEO_SRC = "/hero-bg.mp4";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative min-h-[92vh] flex items-center overflow-hidden pt-20 grain"
      data-testid="hero-section"
    >
      {/* Background: looping video with image poster fallback */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={GALLERY_IMAGES[0].url}
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
          data-testid="hero-bg-video"
        >
          <source src={HERO_VIDEO_SRC} type="video/mp4" />
        </video>
        {/* Dark overlays so the headline stays readable over any frame of the video */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1726] via-[#0B1726]/85 to-[#0B1726]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1726] via-transparent to-[#0B1726]/60" />
      </div>

      {/* Electric blue accent bar */}
      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-64 w-1 bg-[#1E88E5] hidden md:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#1E88E5]" />
            <span className="font-heading text-xs sm:text-sm font-bold tracking-[0.3em] uppercase text-[#E6B445]">
              {COMPANY.tagline}
            </span>
          </div>

          <h1
            className="font-heading text-5xl sm:text-7xl lg:text-8xl font-black uppercase tracking-tighter text-white leading-[0.9]"
            data-testid="hero-headline"
          >
            We don't cut corners.
            <span className="block text-[#1E88E5]">We clean them.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-lg sm:text-xl text-slate-300 leading-relaxed">
            Heavy-duty pressure washing for homes, driveways, commercial lots, fleets
            and restaurant kitchens. Same-week quotes. Fully insured. Guaranteed
            results or we come back.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="/quote"
              data-testid="hero-cta-get-quote"
              className="group bg-[#1E88E5] hover:bg-[#42A5F5] text-white font-heading font-bold tracking-[0.15em] uppercase px-8 py-5 rounded-sm flex items-center justify-center gap-3 transition-all duration-300 shadow-[0_10px_40px_-10px_rgba(0,85,255,0.6)]"
            >
              Get My Free Quote
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href={COMPANY.phoneHref}
              data-testid="hero-cta-call"
              className="bg-transparent border-2 border-white/20 hover:border-white text-white font-heading font-bold tracking-[0.15em] uppercase px-8 py-5 rounded-sm flex items-center justify-center gap-3 transition-all duration-300"
            >
              <Phone className="w-5 h-5" />
              Call or Text {COMPANY.phone}
            </a>
          </div>

          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl">
            {[
              { icon: ShieldCheck, label: "Licensed & Insured" },
              { icon: Star, label: "5-Star Rated" },
              { icon: Wrench, label: "Heavy-Duty Equipment" },
              { icon: ShieldCheck, label: "100% Guarantee" },
            ].map((t, i) => (
              <div key={i} className="flex items-center gap-3 border-l border-white/10 pl-4">
                <t.icon className="w-5 h-5 text-[#1E88E5]" strokeWidth={1.5} />
                <span className="font-heading uppercase text-xs tracking-wider text-slate-300">
                  {t.label}
                </span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
